import React, { useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import Button from '../../../components/ui/Button';

const RevenueChart = () => {
  const [timeframe, setTimeframe] = useState('monthly');
  const [selectedVertical, setSelectedVertical] = useState('all');

  const monthlyData = [
    { month: 'Jan', revenue: 245000, takeRate: 15.2, orders: 1620 },
    { month: 'Feb', revenue: 268000, takeRate: 15.8, orders: 1750 },
    { month: 'Mar', revenue: 289000, takeRate: 15.1, orders: 1890 },
    { month: 'Apr', revenue: 312000, takeRate: 14.9, orders: 2100 },
    { month: 'May', revenue: 298000, takeRate: 15.3, orders: 1980 },
    { month: 'Jun', revenue: 334000, takeRate: 14.7, orders: 2280 },
    { month: 'Jul', revenue: 356000, takeRate: 14.96, orders: 2450 },
    { month: 'Aug', revenue: 378000, takeRate: 15.1, orders: 2580 },
    { month: 'Sep', revenue: 392000, takeRate: 14.8, orders: 2720 },
    { month: 'Oct', revenue: 415000, takeRate: 15.2, orders: 2890 }
  ];

  const weeklyData = [
    { period: 'Week 1', revenue: 89000, takeRate: 14.8, orders: 720 },
    { period: 'Week 2', revenue: 95000, takeRate: 15.1, orders: 780 },
    { period: 'Week 3', revenue: 102000, takeRate: 14.9, orders: 850 },
    { period: 'Week 4', revenue: 108000, takeRate: 15.3, orders: 920 }
  ];

  const verticals = [
    { value: 'all', label: 'All Verticals' },
    { value: 'food', label: 'Food Delivery' },
    { value: 'services', label: 'Home Services' },
    { value: 'property', label: 'Property' },
    { value: 'tours', label: 'Tours & Travel' }
  ];

  const timeframes = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const currentData = timeframe === 'weekly' ? weeklyData : monthlyData;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 elevation-2">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              ></div>
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">
                {entry?.name === 'Take Rate' ? `${entry?.value}%` : 
                 entry?.name === 'Revenue' ? `$${entry?.value?.toLocaleString()}` :
                 entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 elevation-1">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Revenue & Take Rate Analysis</h3>
          <p className="text-sm text-muted-foreground">Revenue trends with commission rate overlay</p>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <div className="flex space-x-2">
            {timeframes?.map((tf) => (
              <Button
                key={tf?.value}
                variant={timeframe === tf?.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeframe(tf?.value)}
              >
                {tf?.label}
              </Button>
            ))}
          </div>
          
          <select 
            value={selectedVertical}
            onChange={(e) => setSelectedVertical(e?.target?.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {verticals?.map((vertical) => (
              <option key={vertical?.value} value={vertical?.value}>
                {vertical?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={currentData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey={timeframe === 'weekly' ? 'period' : 'month'}
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              yAxisId="revenue"
              orientation="left"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `$${(value / 1000)}k`}
            />
            <YAxis 
              yAxisId="rate"
              orientation="right"
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              yAxisId="revenue"
              dataKey="revenue" 
              name="Revenue"
              fill="var(--color-primary)" 
              radius={[4, 4, 0, 0]}
              opacity={0.8}
            />
            <Line 
              yAxisId="rate"
              type="monotone" 
              dataKey="takeRate" 
              name="Take Rate"
              stroke="var(--color-accent)" 
              strokeWidth={3}
              dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: 'var(--color-accent)', strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded"></div>
            <span>Revenue</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-accent rounded"></div>
            <span>Take Rate %</span>
          </div>
        </div>
        
        <Button variant="ghost" size="sm" iconName="Download">
          Export Chart
        </Button>
      </div>
    </div>
  );
};

export default RevenueChart;